/** Validación local (sin SAT/IMSS/RENAPO). Solo formato y consistencia básica. */

const RFC_PF_RE = /^[A-ZÑ&]{4}\d{6}[A-Z0-9]{3}$/i;
const RFC_PM_RE = /^[A-ZÑ&]{3}\d{6}[A-Z0-9]{3}$/i;
const CURP_RE = /^[A-Z]{4}\d{6}[HM][A-Z]{5}[0-9A-Z]\d$/i;
const NSS_RE = /^\d{11}$/;
const CP_RE = /^\d{5}$/;

export const CHECK_ID_STATUS = {
  VALID: "valido",
  INCOMPLETE: "incompleto",
  INCONSISTENT: "inconsistente",
  PENDING: "pendiente",
};

function norm(value) {
  return String(value || "").trim().toUpperCase();
}

export function validateRfcFormat(rfc) {
  const v = norm(rfc);
  if (!v) return { ok: false, message: "RFC vacío" };
  if (RFC_PF_RE.test(v) || RFC_PM_RE.test(v)) return { ok: true, message: "Formato RFC válido" };
  return { ok: false, message: "Formato RFC inválido (12 o 13 caracteres)" };
}

export function validateCurpFormat(curp) {
  const v = norm(curp);
  if (!v) return { ok: false, message: "CURP vacía" };
  if (CURP_RE.test(v)) return { ok: true, message: "Formato CURP válido" };
  return { ok: false, message: "Formato CURP inválido (18 caracteres)" };
}

export function validateNssFormat(nss) {
  const v = String(nss || "").replace(/\D/g, "");
  if (!v) return { ok: false, message: "NSS vacío" };
  if (!NSS_RE.test(v)) return { ok: false, message: "NSS debe tener 11 dígitos" };
  return { ok: true, message: "Formato NSS válido" };
}

export function validateCodigoPostal(cp) {
  const v = String(cp || "").trim();
  if (!v) return { ok: null, message: "Código postal no capturado" };
  if (CP_RE.test(v)) return { ok: true, message: "Código postal con formato válido" };
  return { ok: false, message: "Código postal debe ser 5 dígitos" };
}

/** Coherencia RFC persona física ↔ segmento de CURP (posiciones 1–10). */
export function validateRfcCurpConsistency(rfc, curp) {
  const r = norm(rfc);
  const c = norm(curp);
  if (!r || !c) return { ok: null, message: "Requiere RFC y CURP para cruzar" };
  if (r.length !== 13 || c.length !== 18) {
    return { ok: null, message: "Use RFC de persona física (13) y CURP completa" };
  }
  const rfcKey = r.slice(0, 10);
  const curpKey = c.slice(0, 10);
  if (rfcKey === curpKey) {
    return { ok: true, message: "RFC y CURP coherentes (clave alfabética)" };
  }
  return { ok: false, message: "RFC y CURP no coinciden en clave alfabética" };
}

export function validateNombre(nombre) {
  const v = String(nombre || "").trim();
  if (!v) return { ok: false, message: "Nombre requerido" };
  if (v.length < 3) return { ok: false, message: "Nombre demasiado corto" };
  return { ok: true, message: "Nombre capturado" };
}

export function validateFechaNacimiento(fecha) {
  if (!fecha) return { ok: null, message: "Fecha de nacimiento opcional" };
  const d = new Date(fecha);
  if (Number.isNaN(d.getTime())) return { ok: false, message: "Fecha inválida" };
  if (d > new Date()) return { ok: false, message: "Fecha futura no permitida" };
  return { ok: true, message: "Fecha de nacimiento válida" };
}

export function validateRegimenFiscal(regimen) {
  const v = String(regimen || "").trim();
  if (!v) return { ok: null, message: "Régimen fiscal no capturado" };
  return { ok: true, message: "Régimen fiscal registrado" };
}

/**
 * Ejecuta checklist completo y devuelve estado semáforo.
 * @param {object} fields
 * @returns {{ status: string, checks: Array, summary: string }}
 */
export function runCheckIdLocalValidation(fields) {
  const checks = [
    { id: "nombre", label: "Nombre", ...validateNombre(fields.nombre) },
    { id: "rfc", label: "RFC", ...validateRfcFormat(fields.rfc) },
    { id: "curp", label: "CURP", ...validateCurpFormat(fields.curp) },
    { id: "nss", label: "NSS / Número IMSS", ...validateNssFormat(fields.nss) },
    { id: "rfc_curp", label: "Consistencia RFC–CURP", ...validateRfcCurpConsistency(fields.rfc, fields.curp) },
    { id: "fecha", label: "Fecha de nacimiento", ...validateFechaNacimiento(fields.fechaNacimiento) },
    { id: "cp", label: "C.P. fiscal", ...validateCodigoPostal(fields.codigoPostal) },
    { id: "regimen", label: "Régimen fiscal", ...validateRegimenFiscal(fields.regimenFiscal) },
  ];

  const requiredEmpty = ["nombre", "rfc", "curp"].some(
    (key) => !String(fields[key] || "").trim(),
  );
  const hasFailure = checks.some((c) => c.ok === false);
  const optionalPending = checks.some(
    (c) => !["nombre", "rfc", "curp", "rfc_curp"].includes(c.id) && c.ok === null,
  );
  const consistencyPending = checks.find((c) => c.id === "rfc_curp")?.ok === null;

  let status = CHECK_ID_STATUS.VALID;
  let summary = "Datos listos para continuar con el expediente.";

  if (requiredEmpty) {
    status = CHECK_ID_STATUS.INCOMPLETE;
    summary = "Faltan datos obligatorios (nombre, RFC y CURP).";
  } else if (hasFailure) {
    status = CHECK_ID_STATUS.INCONSISTENT;
    summary = "Hay inconsistencias o formatos inválidos. Corrija antes de inscribir.";
  } else if (optionalPending || consistencyPending) {
    status = CHECK_ID_STATUS.PENDING;
    summary = "Datos básicos correctos; complete NSS, régimen o C.P. para un expediente más sólido.";
  }

  return { status, checks, summary };
}

export function statusMeta(status) {
  const map = {
    [CHECK_ID_STATUS.VALID]: {
      label: "Válido",
      tone: "bg-emerald-50 text-emerald-900 border-emerald-200",
      dot: "bg-emerald-500",
    },
    [CHECK_ID_STATUS.INCOMPLETE]: {
      label: "Incompleto",
      tone: "bg-amber-50 text-amber-900 border-amber-200",
      dot: "bg-amber-500",
    },
    [CHECK_ID_STATUS.INCONSISTENT]: {
      label: "Inconsistente",
      tone: "bg-rose-50 text-rose-900 border-rose-200",
      dot: "bg-rose-500",
    },
    [CHECK_ID_STATUS.PENDING]: {
      label: "Pendiente",
      tone: "bg-sky-50 text-sky-900 border-sky-200",
      dot: "bg-sky-500",
    },
  };
  return map[status] || map[CHECK_ID_STATUS.PENDING];
}
